import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {getState, updateForm, useAutoForm, useSelector} from '../../../store.js'
import {
  useZropdown,
  useZropdownElement,
} from '../../../common/components/Zropdown.js'
import {currentDropdownSelector} from '../../../redux/selectors/ui/index.js'
import FilterDropdown from '../../../common/components/FilterDropdown.js'
import {
  isLabelPropertyEnabledSelector,
  LABEL_PROPERTY_SHIPPER_IDS,
  onLabelPropertyValueChange,
  shipperIDsSelector,
  updateLabelConfig,
  validShippersSelector,
} from '../../../data/labelInfos/index.js'
import {
  SHIPPERS,
  shipperIntegrationIssuesSelector,
  shipperNameSelector,
} from '../../../data/shippers.js'
import {
  filterOptions,
  getNounsFromValue,
  tokenizeOptions,
} from '../../../common/tokenizeOptions.js'
import className from '../../../common/className.js'
import Checkbox from '../../../common/components/Checkbox.js'
import useLabelProperty, {
  EMPTY_DEPENDENT_PROPERTIES_FUNC,
  formatMetaClassName,
} from './useLabelProperty.js'
import EnableLabelProperty, {PickLabelProperty} from './EnableLabelProperty.js'
import {
  Count,
  IfZero,
  IfPluralNotZero,
  IfSingle,
  Plural,
  PluralBlock,
} from '../../../common/components/Plural.js'
import useStable from '../../../common/useStable.js'
import {useLabelConfigContext} from '../LabelConfigContext.js'
import {ERROR_TYPE__FINISH_SETUP} from '../../../common/constants/index.js'
import {SHIPPER_VENDORS} from '../../../common/constants/ShipperNames.js'
import OverrideLabelProperty from './OverrideLabelProperty.js'

const SHIPPERS_FILTER__SELECT_ALL = 'SHIPPERS_FILTER__SELECT_ALL'

export function shippersLabelPropertiesFunc() {
  return [LABEL_PROPERTY_SHIPPER_IDS]
}

function filterShippers(dropdown, shipperOptions, shipperOptionTokens, text) {
  try {
    if (!text) {
      updateForm(dropdown, {
        results: shipperOptions,
        hasFoundNothing: false,
      })

      return
    }

    const results = filterOptions(text, shipperOptionTokens)

    updateForm(dropdown, {
      results,
      selectedIndex: 0,
      hasFoundNothing: results.length === 0,
    })
  } catch (err) {
    updateForm(dropdown, {
      serverError: err.message,
    })
  }
}

function onShipperSelected(localShipperIDs, setLocalShipperIDs, option) {
  const shipperIDs = localShipperIDs.filter((id) => option.entity.id !== id)

  if (shipperIDs.length === localShipperIDs.length) {
    shipperIDs.push(option.entity.id)
  }

  setLocalShipperIDs(shipperIDs)
}

function toggleAllShippers(localShipperIDs, setLocalShipperIDs, option) {
  const allShipperIDs = option.entity.map(({id}) => id)

  let shipperIDs = allShipperIDs.filter((id) => localShipperIDs.includes(id))

  if (shipperIDs.length === allShipperIDs.length) {
    shipperIDs = []
  } else {
    shipperIDs = allShipperIDs
  }

  setLocalShipperIDs(shipperIDs)
}

function commitShipperIDsChange(
  labelInfoID,
  localShipperIDs,
  setLocalShipperIDs,
  localOnChange,
) {
  const validShippers = validShippersSelector(getState(), {labelInfoID})

  // ensure something is selected
  if (localShipperIDs.length === 0 && validShippers.length) {
    localShipperIDs = [validShippers[0].id]
  }

  updateLabelConfig(labelInfoID, {shipper_ids: localShipperIDs})
  setLocalShipperIDs(localShipperIDs)

  localOnChange()
}

function Item({option, selected, disabled, isFocused, onSelect}) {
  const ref = useRef()

  useZropdownElement({ref, isSelected: isFocused})

  return (
    <li ref={ref} className="list__item list__item--dropdown">
      <span
        className={className`label--selectable label--selectable-pt-pb-0 flex--justify-nowrap margin-bottom-0 ${{
          'list__link--dropdown-arrow-focus': isFocused,
          'label--selectable-warning': option.integrationIssue,
          disabled,
        }}`}
      >
        <label
          className="flex--justify-nowrap flex-grow flex--jc-left mw-unset align-items-center margin-bottom-0"
          htmlFor={`shipper__${option.entity.id}`}
          data-dropdown-prevent-close
        >
          <span>
            <Checkbox
              mode="fancy"
              className="fs-00 v-align-middle"
              id={`shipper__${option.entity.id}`}
              checked={selected}
              onClick={() => {
                if (!disabled) {
                  onSelect(option)
                }
              }}
            />
          </span>
          <span
            className={className`label--selectable__text label__carrier-logo fs-n1 lh-sm unbold ${option.entity.vendor}`}
          >
            <span className="label__carrier-name">{option.display}</span>
          </span>
        </label>
        {option.integrationIssue && (
          <a
            className="btn btn--carrier-reauth-callout flex flex--justify-col margin-left-7"
            href={option.integrationIssue.link}
          >
            <strong className="fs-n1 lh-md">
              {option.integrationIssue.errorType === ERROR_TYPE__FINISH_SETUP
                ? 'FINISH SETUP'
                : 'REAUTHORIZE'}
            </strong>
          </a>
        )}
      </span>
    </li>
  )
}

function SelectAll({option, selected, indeterminate, isFocused, onSelect}) {
  const ref = useRef()

  useZropdownElement({ref, isSelected: isFocused})

  return (
    <li
      ref={ref}
      className="list__item list__item--dropdown list__item--dropdown-header list__item--dropdown-header-pkg-type"
    >
      <span
        className={className`label--selectable label--selectable-pt-pb-0 flex--justify-nowrap margin-bottom-0 ${{
          'list__link--dropdown-arrow-focus': isFocused,
        }}`}
      >
        <label
          className="flex--justify-nowrap flex-grow flex--jc-left mw-unset align-items-center  margin-bottom-0"
          htmlFor="shippers_filter_select_all"
          data-dropdown-prevent-close
        >
          <span>
            <Checkbox
              mode="fancy"
              className="fs-00 v-align-middle"
              id="shippers_filter_select_all"
              checked={selected}
              indeterminate={indeterminate}
              onClick={() => onSelect(option)}
            />
          </span>
          <span className="label--selectable__text lh-sm margin-left-10">
            Select {selected && !indeterminate ? 'None' : 'All'}
          </span>
        </label>
      </span>
    </li>
  )
}

/**
 * Shipper filter for label info conf
 *
 * @param {Object} props
 * @param {Boolean} props.autoFocus
 * @returns {React.ReactNode}
 */
export default function ShippersFilter({autoFocus}) {
  const {contextID, labelInfoID, onChange, isSummary, isPickMode} =
    useLabelConfigContext()
  const propertySpecs = ShippersFilter.propertySpecList[0]
  const [labelProperty] = propertySpecs.labelPropertiesFunc()

  const dropdown = `SHIPPERS_FILTER_${contextID}_${labelInfoID}`
  const form = useAutoForm(dropdown, {
    text: '',
    results: [],
    selectedIndex: 0,
    hasFoundNothing: false,
    serverError: null,
    hasChanged: false,
  })
  const localOnChange = useCallback(() => {
    onLabelPropertyValueChange(labelInfoID, labelProperty)

    if (onChange) {
      onChange()
    }
  }, [])
  const validShippers = useStable(
    useSelector((state) => validShippersSelector(state, {labelInfoID})),
  )
  const shipperIntegrationIssues = useStable(
    useSelector(shipperIntegrationIssuesSelector),
  )
  const validShipperOptions = useMemo(() => {
    const options = []

    for (const shipper of validShippers) {
      const integrationIssue = shipperIntegrationIssues.find(
        ({id}) => shipper.id === id,
      )

      options.push({
        value: shipper.id,
        display: shipper.name,
        entity: shipper,
        type: SHIPPERS,
        nouns: [...getNounsFromValue(shipper.name), shipper.vendor],
        integrationIssue,
      })
    }

    if (options.length > 1) {
      options.unshift({
        value: SHIPPERS_FILTER__SELECT_ALL,
        display: 'select all',
        entity: options.map(({entity}) => entity),
        type: SHIPPERS_FILTER__SELECT_ALL,
        nouns: new Set(
          options.reduce((prev, {nouns}) => {
            prev.push(...nouns)
            return prev
          }, []),
        ),
      })
    }

    return options
  }, [validShippers, shipperIntegrationIssues])
  const validShipperOptionTokens = useMemo(
    () => tokenizeOptions(validShipperOptions),
    [validShipperOptions],
  )
  const [localShipperIDs, setLocalShipperIDs] = useState([])
  const shipperIDs = useStable(
    useSelector((state) => shipperIDsSelector(state, {labelInfoID})),
  )
  const isLabelPropertyEnabled = useSelector((state) =>
    isLabelPropertyEnabledSelector(state, {
      labelInfoID,
      labelProperty,
    }),
  )

  useEffect(() => {
    setLocalShipperIDs(shipperIDs)
  }, [shipperIDs])

  const firstShipperName = useSelector((state) =>
    shipperNameSelector(state, {shipperID: localShipperIDs[0]}),
  )
  const selectedCount = localShipperIDs.length

  const isAllSelected = selectedCount === validShippers.length

  useEffect(() => {
    filterShippers(
      dropdown,
      validShipperOptions,
      validShipperOptionTokens,
      form && form.text,
    )
  }, [validShipperOptions, form && form.text])

  const buttonRef = useRef()

  const {onChangeInternal, onSelectInternal, onConfirm, onUp, onDown} =
    useZropdown({
      dropdown,
      form,
      onSelect: (option) => {
        if (option.type === SHIPPERS) {
          onShipperSelected(localShipperIDs, setLocalShipperIDs, option)
        } else {
          toggleAllShippers(localShipperIDs, setLocalShipperIDs, option)
        }

        updateForm(dropdown, {hasChanged: true})
      },
      autoFocus,
    })

  const currentDropdown = useSelector(currentDropdownSelector)

  useEffect(() => {
    if (!currentDropdown && form.hasChanged) {
      updateForm(dropdown, {hasChanged: false})

      commitShipperIDsChange(
        labelInfoID,
        localShipperIDs,
        setLocalShipperIDs,
        localOnChange,
      )
    }
  }, [currentDropdown])

  useLabelProperty({
    labelProperty,
    propertySpecs,
  })

  if (isSummary) {
    return 'Carrier'
  }

  const overrideLabel =
    'Override other selected carriers when preset is applied'
  const overrideHelpLink =
    'https://support.ordoro.com/how-do-additive-vs-overwrite-presets-work-in-rate-shopping/'
  const overrideHelpTitle = 'Learn More'

  if (isPickMode) {
    return (
      <PickLabelProperty
        label="Assign Carrier"
        labelProperty={labelProperty}
        overrideLabel={isLabelPropertyEnabled ? overrideLabel : null}
        propertySpecs={propertySpecs}
        overrideHelpLink={overrideHelpLink}
        overrideHelpTitle={overrideHelpTitle}
      />
    )
  }

  return (
    <li
      className={className`list__item--shipping-options list__item--shipping-options-rs flex-grow ${formatMetaClassName(
        labelProperty,
      )}`}
    >
      <div className="wrap--edit-preset-form-input wrap--edit-preset-prevent-disable">
        <label className="label--bold" htmlFor={dropdown}>
          <span>Carrier</span>
          <span className="required">*</span>
        </label>
        <div
          className={`wrap-outer--tag-dropdown-with-filter ${
            currentDropdown === dropdown ? 'open' : ''
          }`}
          data-dropdown={dropdown}
        >
          <button
            id={dropdown}
            ref={buttonRef}
            className="btn btn--dropdown btn--filter btn--filter-w-border w-100"
            type="button"
          >
            <span className="btn__text flex">
              <span className="btn__text--no-overflow txt-overflow-ellipsis">
                <span className="txt-overflow-ellipsis">
                  <span>
                    <PluralBlock count={selectedCount}>
                      <IfZero>
                        <em>Select a carrier...</em>
                      </IfZero>
                      <IfSingle>{firstShipperName}</IfSingle>
                      <IfPluralNotZero>
                        {isAllSelected ? (
                          'All carriers'
                        ) : (
                          <>
                            <Count /> <Plural word="carrier" />
                          </>
                        )}
                      </IfPluralNotZero>
                    </PluralBlock>
                  </span>
                </span>
              </span>
            </span>
          </button>
          <FilterDropdown
            dropdown={dropdown}
            form={form}
            onChangeInternal={onChangeInternal}
            onConfirm={onConfirm}
            onUp={onUp}
            onDown={onDown}
            placeholder="Search your carriers"
            includeSearchInput
            parentRef={buttonRef}
          >
            {form.results.map((option, index) =>
              option.type === SHIPPERS ? (
                <Item
                  key={option.value}
                  option={option}
                  selected={localShipperIDs.includes(option.entity.id)}
                  isFocused={index === form.selectedIndex}
                  onSelect={onSelectInternal}
                />
              ) : (
                <SelectAll
                  key={option.value}
                  option={option}
                  selected={selectedCount > 0}
                  indeterminate={
                    option.entity.length !== selectedCount &&
                    selectedCount !== 0
                  }
                  isFocused={index === form.selectedIndex}
                  onSelect={onSelectInternal}
                />
              ),
            )}
            {form.hasFoundNothing && (
              <li className="list__item--dropdown list__item--dropdown-empty align-center">
                No carrier match your search.
              </li>
            )}
          </FilterDropdown>
        </div>
      </div>
      <EnableLabelProperty labelProperty={labelProperty} />
      <OverrideLabelProperty
        propertySpecs={propertySpecs}
        label={overrideLabel}
        helpLink={overrideHelpLink}
        helpTitle={overrideHelpTitle}
      />
    </li>
  )
}

ShippersFilter.propertySpecList = [
  {
    validShipperTypes: SHIPPER_VENDORS,
    labelPropertiesFunc: shippersLabelPropertiesFunc,
    dependentPropertiesFunc: EMPTY_DEPENDENT_PROPERTIES_FUNC,
    validLabelPropertiesFunc: () => [LABEL_PROPERTY_SHIPPER_IDS],
  },
]
